import { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr, API } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { TASK_STATUS_STYLES, money } from "../lib/constants";
import { toast } from "sonner";
import { Download, Upload, Plus, Trash2, AlertTriangle, SlidersHorizontal } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ImportExport } from "../components/ImportExport";
import { Gantt } from "../components/Gantt";

const TABS = ["WBS", "Timeline", "RAID Log", "Resourcing", "Budget"];
const DEFAULT_COLS = { meta: true, planned: true, actual: true, variance: true, percent: false, status: true };
const COL_OPTIONS = [["meta", "Owner & dates"], ["planned", "Planned $"], ["actual", "Actual $"], ["variance", "Variance"], ["percent", "% complete"], ["status", "Status"]];

export default function ProjectPlan() {
  const { effectiveRole } = useAuth();
  const canEdit = ["pmo", "it_admin"].includes(effectiveRole);
  const [tab, setTab] = useState("WBS");
  const [ws, setWs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [raid, setRaid] = useState([]);
  const [budget, setBudget] = useState([]);
  const [users, setUsers] = useState([]);
  const [wbsCols, setWbsCols] = useState(() => { try { return JSON.parse(localStorage.getItem("wbs_cols")) || null; } catch { return null; } });
  const [showColMenu, setShowColMenu] = useState(false);
  const cols = { ...DEFAULT_COLS, ...(wbsCols || {}) };
  const toggleCol = (k) => { const next = { ...cols, [k]: !cols[k] }; setWbsCols(next); localStorage.setItem("wbs_cols", JSON.stringify(next)); };
  const fileRef = useRef();

  const load = () => {
    api.get("/workstreams").then((r) => setWs(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/raid").then((r) => setRaid(r.data)).catch(() => {});
    api.get("/budget").then((r) => setBudget(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const wsCode = (wid) => ws.find((w) => w.id === wid)?.code || "?";
  const wsTitle = (wid) => ws.find((w) => w.id === wid)?.title || "";
  const userName = (uid) => users.find((u) => u.id === uid)?.name || "Unassigned";

  const exportCsv = async () => {
    try {
      const r = await api.get("/tasks-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "project_plan_tasks.csv"; a.click();
      toast.success("Exported CSV");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text();
    try { const r = await api.post("/tasks-import", { csv: text }); toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated`); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const addTask = async () => {
    const title = prompt("Task title:"); if (!title) return;
    const code = prompt(`Workstream code (${ws.map((w) => w.code).join(", ")}):`, ws[0]?.code);
    const w = ws.find((x) => x.code === code); if (!w) { toast.error("Unknown workstream"); return; }
    try { await api.post("/tasks", { workstream_id: w.id, title }); toast.success("Task added"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delTask = async (id) => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${id}`); load(); };
  const patchDates = async (id, changes) => { try { await api.patch(`/tasks/${id}`, changes); toast.success("Rescheduled"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const patchCost = async (id, field, val) => { try { await api.patch(`/tasks/${id}`, { [field]: Number(val) || 0 }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addSubtask = async (parent) => { const title = prompt("Subtask title:"); if (!title) return; try { await api.post("/tasks", { workstream_id: parent.workstream_id, title, parent_task_id: parent.id }); toast.success("Subtask added"); load(); } catch (e) { toast.error(apiErr(e)); } };

  // Resourcing
  const allocation = {};
  tasks.forEach((t) => { if (t.assignee_id && t.status !== "Completed") allocation[t.assignee_id] = (allocation[t.assignee_id] || 0) + 1; });

  // Timeline bounds
  const dates = tasks.flatMap((t) => [t.start_date, t.end_date]).filter(Boolean).map((d) => new Date(d));
  const minD = dates.length ? new Date(Math.min(...dates)) : new Date();
  const maxD = dates.length ? new Date(Math.max(...dates)) : new Date();
  const span = Math.max(1, (maxD - minD) / 86400000);
  const barStyle = (t) => {
    if (!t.start_date || !t.end_date) return { left: "0%", width: "10%" };
    const l = ((new Date(t.start_date) - minD) / 86400000 / span) * 100;
    const wdt = Math.max(3, ((new Date(t.end_date) - new Date(t.start_date)) / 86400000 / span) * 100);
    return { left: `${l}%`, width: `${wdt}%` };
  };

  const budgetByWs = {};
  budget.forEach((b) => {
    const c = wsCode(b.workstream_id);
    budgetByWs[c] = budgetByWs[c] || { name: c, Planned: 0, Actual: 0 };
    budgetByWs[c].Planned += b.planned_amount; budgetByWs[c].Actual += b.actual_amount;
  });
  const totalPlanned = budget.reduce((a, b) => a + b.planned_amount, 0);
  const totalActual = budget.reduce((a, b) => a + b.actual_amount, 0);
  const capital = budget.filter((b) => b.capital).reduce((a, b) => a + b.planned_amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">Comprehensive Project Plan</h1>
          <p className="text-[#5A687C] mt-1">Work breakdown, timeline, RAID log, resourcing, and budget.</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 bg-[#F4F6FA] hover:bg-[#DCE2EA] border border-[#DCE2EA] rounded-full px-4 py-2 text-sm font-medium"><Download className="h-4 w-4" /> Export CSV</button>
            <button data-testid="import-csv-btn" onClick={() => fileRef.current.click()} className="inline-flex items-center gap-1.5 bg-[#F4F6FA] hover:bg-[#DCE2EA] border border-[#DCE2EA] rounded-full px-4 py-2 text-sm font-medium"><Upload className="h-4 w-4" /> Import CSV</button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 bg-[#F4F6FA] p-1.5 rounded-full border border-[#DCE2EA] w-fit overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`plan-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${tab === t ? "bg-[#BA0C2F] text-white font-semibold" : "text-[#5A687C] hover:bg-white/70 font-medium"}`}>{t}</button>
        ))}
      </div>

      {tab === "WBS" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1F2733]">Work Breakdown Structure</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button data-testid="wbs-columns-btn" onClick={() => setShowColMenu((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A687C] hover:text-[#1F2733] border border-[#DCE2EA] rounded-full px-3 py-1.5"><SlidersHorizontal className="h-4 w-4" /> Columns</button>
                {showColMenu && (
                  <div className="absolute right-0 mt-2 z-20 bg-white border border-[#DCE2EA] rounded-2xl shadow-lg p-3 w-52" data-testid="wbs-columns-menu">
                    {COL_OPTIONS.map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm text-[#1F2733] py-1.5 cursor-pointer">
                        <input type="checkbox" data-testid={`wbs-col-${k}`} checked={cols[k]} onChange={() => toggleCol(k)} /> {label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {canEdit && <button data-testid="add-task-btn" onClick={addTask} className="inline-flex items-center gap-1 text-sm font-semibold text-[#BA0C2F]"><Plus className="h-4 w-4" /> Add task</button>}
            </div>
          </div>
          {(() => { const p = tasks.reduce((a, t) => a + (t.planned_cost || 0), 0); const ac = tasks.reduce((a, t) => a + (t.actual_cost || 0), 0); return (
          <div className="flex flex-wrap gap-3 mb-4 text-sm" data-testid="wbs-rollup">
            <span className="px-3 py-1.5 rounded-full bg-[#F4F6FA] border border-[#DCE2EA]">Program planned <b>{money(p)}</b></span>
            <span className="px-3 py-1.5 rounded-full bg-[#F4F6FA] border border-[#DCE2EA]">Actual <b>{money(ac)}</b></span>
            <span className={`px-3 py-1.5 rounded-full border ${ac - p > 0 ? "bg-[#FDF2F4] text-[#8A0822] border-[#F8C4CC]" : "bg-[#ECF8F1] text-[#1A5633] border-[#A3E2BB]"}`}>Variance <b>{money(ac - p)}</b></span>
          </div>
          ); })()}
          <div className="flex items-center gap-2 pb-2 mb-2 border-b border-[#DCE2EA] text-[10px] font-bold uppercase tracking-wide text-[#98A2AF]" data-testid="wbs-header">
            <div className="flex-1 min-w-0">Task</div>
            {cols.planned && <div className="w-24 text-right shrink-0">Planned</div>}
            {cols.actual && <div className="w-24 text-right shrink-0">Actual</div>}
            {cols.variance && <div className="w-20 text-right shrink-0">Variance</div>}
            {cols.percent && <div className="w-14 text-right shrink-0">%</div>}
            {cols.status && <div className="w-32 text-right shrink-0">Status</div>}
            {canEdit && <div className="w-16 shrink-0" />}
          </div>
          {ws.map((w) => {
            const wt = tasks.filter((t) => t.workstream_id === w.id);
            if (!wt.length) return null;
            return (
              <div key={w.id} className="mb-4">
                <div className="flex items-center gap-2 font-bold text-[#1F2733] text-sm"><span className="text-white bg-[#BA0C2F] rounded-full px-2 py-0.5 text-xs">{w.code}</span> {w.title}
                  <span className="ml-auto font-normal text-[11px] text-[#5A687C]">Planned {money(wt.reduce((a, x) => a + (x.planned_cost || 0), 0))} · Actual {money(wt.reduce((a, x) => a + (x.actual_cost || 0), 0))}</span>
                </div>
                <div className="mt-2 ml-3 border-l-2 border-[#DCE2EA] pl-4 space-y-1.5">
                  {wt.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 py-1.5" style={{ paddingLeft: t.parent_task_id ? 20 : 0 }}>
                      <div className="flex-1 min-w-0"><div className="text-sm text-[#1F2733] truncate">{t.parent_task_id ? "↳ " : ""}{t.title}</div>
                        {cols.meta && <div className="text-[11px] text-[#98A2AF]">{userName(t.assignee_id)} · {t.start_date} → {t.end_date}</div>}</div>
                      {cols.planned && (canEdit
                        ? <input type="number" defaultValue={t.planned_cost || 0} onBlur={(e) => patchCost(t.id, "planned_cost", e.target.value)} className="w-24 shrink-0 px-2 py-1 rounded-lg border border-[#DCE2EA] text-xs text-right" title="Planned $" />
                        : <span className="w-24 shrink-0 text-xs text-right text-[#5A687C]">{money(t.planned_cost || 0)}</span>)}
                      {cols.actual && (canEdit
                        ? <input type="number" defaultValue={t.actual_cost || 0} onBlur={(e) => patchCost(t.id, "actual_cost", e.target.value)} className="w-24 shrink-0 px-2 py-1 rounded-lg border border-[#DCE2EA] text-xs text-right" title="Actual $" />
                        : <span className="w-24 shrink-0 text-xs text-right text-[#5A687C]">{money(t.actual_cost || 0)}</span>)}
                      {cols.variance && <span className={`w-20 shrink-0 text-xs text-right font-semibold ${((t.actual_cost || 0) - (t.planned_cost || 0)) > 0 ? "text-[#BA0C2F]" : "text-[#2E8B57]"}`} title="Variance">{money((t.actual_cost || 0) - (t.planned_cost || 0))}</span>}
                      {cols.percent && <span className="w-14 shrink-0 text-xs text-right text-[#5A687C]" title="% complete">{t.percent_complete || 0}%</span>}
                      {cols.status && <div className="w-32 shrink-0 flex justify-end"><span className={`text-xs font-semibold px-3 py-1 rounded-full border whitespace-nowrap ${TASK_STATUS_STYLES[t.status] || ""}`}>{t.status}</span></div>}
                      {canEdit && <div className="w-16 shrink-0 flex justify-end gap-1">
                        <button onClick={() => addSubtask(t)} className="text-[#BA0C2F] text-xs font-bold px-1" title="Add subtask">+Sub</button>
                        <button onClick={() => delTask(t.id)} className="text-[#98A2AF] hover:text-[#BA0C2F]"><Trash2 className="h-4 w-4" /></button>
                      </div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "Timeline" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] mb-1">Gantt Timeline</h2>
          <p className="text-xs text-[#5A687C] mb-4">{minD.toLocaleDateString()} → {maxD.toLocaleDateString()} · dependency lines &amp; critical path shown</p>
          <Gantt tasks={tasks} wsCode={wsCode} canEdit={canEdit} onPatch={patchDates} />
        </div>
      )}

      {tab === "RAID Log" && (
        <div className="space-y-3">
          {canEdit && <div className="flex justify-end"><ImportExport collection="raid" onDone={load} /></div>}
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F4F6FA] text-[#5A687C] text-xs font-bold uppercase tracking-wider">
              <th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Description</th>
              <th className="text-left px-4 py-3">Workstream</th><th className="text-center px-4 py-3">L×I</th>
              <th className="text-left px-4 py-3">Mitigation plan</th><th className="text-left px-4 py-3">Status</th>
            </tr></thead>
            <tbody>
              {raid.map((r) => (
                <tr key={r.id} className="border-b border-[#DCE2EA]/60 hover:bg-[#F4F6FA]/60">
                  <td className="px-4 py-3"><span className="text-xs font-bold uppercase px-2.5 py-1 rounded-full bg-[#FBEAED] text-[#8A0822]">{r.type}</span></td>
                  <td className="px-4 py-3 max-w-xs">{r.description}</td>
                  <td className="px-4 py-3 text-[#5A687C]">{wsCode(r.workstream_id)}</td>
                  <td className="px-4 py-3 text-center font-bold" style={{ color: r.likelihood * r.impact >= 15 ? "#BA0C2F" : r.likelihood * r.impact >= 8 ? "#C97A1B" : "#2E8B57" }}>{r.likelihood}×{r.impact}</td>
                  <td className="px-4 py-3 text-[#5A687C] max-w-xs">{r.mitigation_plan}</td>
                  <td className="px-4 py-3">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "Resourcing" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] mb-4">Resourcing &amp; Capacity</h2>
          <div className="space-y-2">
            {users.filter((u) => ["it_executive", "engineer", "pmo"].includes(u.role)).map((u) => {
              const load = allocation[u.id] || 0;
              const over = load > 3;
              return (
                <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
                  <div className="h-9 w-9 rounded-full bg-[#FBEAED] text-[#BA0C2F] flex items-center justify-center text-xs font-bold">{u.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                  <div className="flex-1"><div className="text-sm font-medium text-[#1F2733]">{u.name}</div><div className="text-[11px] text-[#98A2AF]">{u.title}</div></div>
                  <div className="w-40 h-2 bg-[#F1F3F6] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, load * 25)}%`, background: over ? "#BA0C2F" : "#BA0C2F" }} /></div>
                  <div className="text-sm font-semibold w-20 text-right">{load} active</div>
                  {over && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#BA0C2F]"><AlertTriangle className="h-3.5 w-3.5" /> Over</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "Budget" && (
        <div className="space-y-5">
          {canEdit && <div className="flex justify-end"><ImportExport collection="budget" onDone={load} /></div>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Planned", totalPlanned], ["Actual", totalActual], ["Remaining", totalPlanned - totalActual], ["Capital", capital]].map(([l, v]) => (
              <div key={l} className="bg-white rounded-[18px] border border-[#DCE2EA] p-5"><div className="text-2xl font-extrabold text-[#BA0C2F]">{money(v)}</div><div className="text-xs text-[#5A687C] mt-1 font-medium">{l}</div></div>
            ))}
          </div>
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <h2 className="font-bold text-[#1F2733] mb-4">Planned vs Actual by workstream</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={Object.values(budgetByWs)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCE2EA" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#5A687C" }} /><YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#5A687C" }} />
                <Tooltip formatter={(v) => money(v)} /><Legend />
                <Bar dataKey="Planned" fill="#F1B9C3" radius={[6, 6, 0, 0]} /><Bar dataKey="Actual" fill="#BA0C2F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
